import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Loader2, AlertCircle, CheckCircle2, Clock, Pencil, Lock, RotateCcw, Plus } from 'lucide-react';
import {
  NAVY, PageAssu, FormuleCarte, PiecesJointes, Recapitulatif, BlocValidation, ChampNomCrm,
  titreEtape, numeroEtape, encadreErreur
} from './FormuleAssu';

// Parcours public complet, sur une seule page : lire les formules, en choisir une,
// laisser ses coordonnées, déposer ses pièces, relire son récapitulatif et valider.
// Le dossier est créé en base dès l'étape des coordonnées, ce qui permet ensuite
// d'envoyer les fichiers ; le candidat, lui, ne quitte jamais la page.

const CLE_REPRISE = 'dossier_assu_token';
// La clé d'accès arrive par l'adresse : `/c/<clé>` (forme courte) ou `?k=<clé>`.
// Elle RESTE dans l'adresse : l'effacer cassait le rafraîchissement, le retour
// arrière et les favoris, et obligeait à renvoyer un lien à chaque fois. Elle est
// aussi gardée dans le navigateur, pour que la page se rouvre sans le lien.
const CLE_STOCKAGE = 'cle_conditions_assu';

const memoriserCle = (valeur) => {
  try { localStorage.setItem(CLE_STOCKAGE, valeur); } catch (_) { /* navigation privée */ }
  try { sessionStorage.setItem(CLE_STOCKAGE, valeur); } catch (_) { /* ignoré */ }
};

const lireCleInitiale = () => {
  try {
    // Adresse courte `/c/<clé>`, ou forme `?k=<clé>`.
    const dansChemin = window.location.pathname.match(/^\/c\/([^/?#]+)/);
    const depuisUrl = (dansChemin && decodeURIComponent(dansChemin[1]))
      || new URLSearchParams(window.location.search).get('k');
    if (depuisUrl) {
      memoriserCle(depuisUrl);
      return depuisUrl;
    }
    let gardee = '';
    try { gardee = sessionStorage.getItem(CLE_STOCKAGE) || ''; } catch (_) { /* ignoré */ }
    if (!gardee) {
      try { gardee = localStorage.getItem(CLE_STOCKAGE) || ''; } catch (_) { /* ignoré */ }
    }
    return gardee;
  } catch (_) {
    return '';
  }
};

function ConditionsAssu() {
  const API_URL = process.env.REACT_APP_API_URL || '';

  const [code, setCode] = useState(lireCleInitiale);
  const [saisieCode, setSaisieCode] = useState('');
  const [codeOk, setCodeOk] = useState(null); // null = en cours, false = à saisir, true = ouvert
  const [formules, setFormules] = useState(null);
  const [repriseToken, setRepriseToken] = useState(null);
  const [choix, setChoix] = useState(null);
  const [form, setForm] = useState({ nom: '', nom_crm: '', telephone_whatsapp: '', email_pro: '' });
  const [token, setToken] = useState(null);
  const [data, setData] = useState(null);
  const [accepte, setAccepte] = useState(false);
  const [occupe, setOccupe] = useState(null);
  const [erreur, setErreur] = useState('');
  const [manquants, setManquants] = useState([]);
  // Vrai quand les coordonnées saisies ont ramené un dossier déjà ouvert.
  const [dossierRetrouve, setDossierRetrouve] = useState(false);

  const dateFr = (d) => d ? new Date(d).toLocaleString('fr-FR', {
    day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) : null;

  // Reprise d'un dossier déjà ouvert depuis cet appareil. Volontairement MANUELLE :
  // le lien doit permettre d'enregistrer autant de demandes qu'on veut, il ne doit
  // donc jamais rouvrir tout seul la précédente.
  const reprendre = useCallback(async (t) => {
    try {
      const res = await axios.get(`${API_URL}/api/candidats-assu/dossier/${t}`);
      setToken(t);
      setData(res.data);
      setChoix(res.data.formule_id);
      setAccepte(!!res.data.conditions_acceptees);
      setRepriseToken(null);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (_) {
      setRepriseToken(null);
      try { localStorage.removeItem(CLE_REPRISE); } catch (__) { /* ignoré */ }
    }
  }, [API_URL]);

  // Repart d'une page vierge pour saisir une nouvelle demande.
  const nouvelleDemande = () => {
    setToken(null);
    setData(null);
    setDossierRetrouve(false);
    setChoix(null);
    setForm({ nom: '', nom_crm: '', telephone_whatsapp: '', email_pro: '' });
    setAccepte(false);
    setErreur('');
    setManquants([]);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (!code) { setCodeOk(false); return; }
    axios.get(`${API_URL}/api/candidats-assu/formules`, { headers: { 'X-Cle-Acces': code } })
      .then(res => { setFormules(res.data.formules); setCodeOk(true); })
      .catch(err => {
        if (err.response?.status === 403) setCodeOk(false);
        else { setFormules([]); setCodeOk(true); }
      });
  }, [API_URL, code]);

  useEffect(() => {
    try {
      const t = localStorage.getItem(CLE_REPRISE);
      if (t) setRepriseToken(t);
    } catch (_) { /* navigation privée : on s'en passe */ }
  }, []);

  const coordonneesCompletes = () =>
    form.nom.trim().length >= 2
    && (form.telephone_whatsapp.match(/\d/g) || []).length >= 8
    && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email_pro.trim());

  // Le dossier s'ouvre tout seul dès que la formule est cochée et les trois
  // champs remplis : le candidat n'a pas d'étape intermédiaire à valider avant
  // de pouvoir envoyer ses pièces.
  // Renvoie le jeton du dossier, en le créant au besoin. Appelé aussi bien à la
  // sortie d'un champ qu'au moment de valider : quel que soit l'ordre dans lequel
  // le candidat remplit la page, le dossier finit par exister.
  const assurerDossier = async (formuleId = choix) => {
    if (token) return token;
    if (!formuleId || !coordonneesCompletes()) return null;
    setOccupe('ouverture');
    setErreur('');
    try {
      // Avant d'en ouvrir un second : ce candidat a-t-il déjà un dossier ? Les
      // trois coordonnées doivent coïncider, sinon on n'a rien retrouvé du tout.
      // C'est ce qui évite d'avoir à renvoyer un lien pour reprendre un dossier.
      let jeton = null;
      let reprise = false;
      try {
        const cherche = await axios.post(`${API_URL}/api/candidats-assu/dossier/retrouver`,
          { ...form, cle: code });
        if (cherche.data.trouve) { jeton = cherche.data.token; reprise = true; }
      } catch (_) { /* la recherche échoue : on ouvre un dossier neuf */ }

      if (!jeton) {
        const res = await axios.post(`${API_URL}/api/candidats-assu/dossier/public`,
          { ...form, formule_id: formuleId, cle: code });
        jeton = res.data.token;
      }

      try { localStorage.setItem(CLE_REPRISE, jeton); } catch (_) { /* ignoré */ }
      setToken(jeton);

      let suite = await axios.get(`${API_URL}/api/candidats-assu/dossier/${jeton}`);
      // Dossier retrouvé, mais le candidat vient de cocher une autre formule :
      // c'est son choix du moment qui compte, tant qu'il peut encore le changer.
      if (reprise && suite.data.formule_id !== formuleId
          && ['envoye', 'consulte', 'en_cours', 'refuse'].includes(suite.data.statut)) {
        try {
          const maj = await axios.post(`${API_URL}/api/candidats-assu/dossier/${jeton}/formule`,
            { formule_id: formuleId });
          suite = { data: { ...suite.data, ...maj.data } };
        } catch (_) { /* on garde la formule déjà enregistrée */ }
      }
      setData(suite.data);
      setChoix(suite.data.formule_id || formuleId);
      setDossierRetrouve(reprise);
      return jeton;
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur lors de l\'enregistrement de vos coordonnées');
      return null;
    } finally {
      setOccupe(null);
    }
  };

  const ouvrirDossier = () => { assurerDossier(); };

  const changerFormule = async (formuleId) => {
    setChoix(formuleId);
    // Formule cochée après avoir rempli ses coordonnées : on ouvre le dossier ici,
    // sinon plus aucun événement ne viendrait le déclencher.
    if (!token) { assurerDossier(formuleId); return; }
    if (!data || formuleId === data.formule_id) return;
    setOccupe('formule');
    try {
      const res = await axios.post(`${API_URL}/api/candidats-assu/dossier/${token}/formule`, { formule_id: formuleId });
      setData(res.data);
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur lors du changement de formule');
    } finally {
      setOccupe(null);
    }
  };

  const valider = async () => {
    const jeton = await assurerDossier();
    if (!jeton) {
      setErreur('Vérifiez votre formule et vos coordonnées avant de valider.');
      return;
    }
    setOccupe('validation');
    setErreur('');
    try {
      const res = await axios.post(`${API_URL}/api/candidats-assu/dossier/${jeton}/soumettre`, {
        conditions_acceptees: accepte
      });
      setData(prev => ({ ...prev, ...res.data }));
      setManquants([]);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setErreur(err.response?.data?.error || 'Erreur lors de l\'envoi de votre candidature');
      setManquants(err.response?.data?.manquants || []);
    } finally {
      setOccupe(null);
    }
  };

  if (codeOk === false) {
    return (
      <PageAssu titre="Nos conditions de rémunération" sousTitre="Accès réservé">
        <div className="dca-card">
          <div style={{ display: 'flex', gap: 11, alignItems: 'flex-start', marginBottom: 16 }}>
            <Lock size={20} style={{ color: NAVY, flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 14.5 }}>
              Cette page est réservée. Ouvrez-la depuis le lien complet que nous vous
              avons envoyé, ou collez ci-dessous la clé qui s'y trouve.
            </div>
          </div>
          <form onSubmit={(e) => {
            e.preventDefault();
            const propre = saisieCode.trim();
            setCodeOk(null);
            memoriserCle(propre);
            setCode(propre);
          }}>
            <input
              className="dca-champ"
              type="text"
              autoFocus
              placeholder="Clé d'accès"
              value={saisieCode}
              onChange={(e) => setSaisieCode(e.target.value)}
              style={{ marginBottom: 13, fontWeight: 600 }}
            />
            <button
              type="submit"
              className="dca-btn"
              style={{ width: '100%', justifyContent: 'center', background: NAVY, color: '#fff' }}
            >
              Accéder
            </button>
          </form>
          {code && (
            <div style={{ ...encadreErreur, marginBottom: 0 }}>
              Clé invalide ou expirée. Demandez-nous un nouveau lien.
            </div>
          )}
        </div>
      </PageAssu>
    );
  }

  if (formules === null) {
    return (
      <PageAssu titre="Nos conditions" sousTitre="Un instant…">
        <div className="dca-card" style={{ textAlign: 'center', color: '#5f6b85' }}>
          <Loader2 size={26} className="dca-spin" style={{ color: NAVY }} />
          <div style={{ marginTop: 10 }}>Chargement des conditions…</div>
        </div>
      </PageAssu>
    );
  }

  // ---- Candidature déjà envoyée : le candidat relit ce qu'il a transmis ----
  if (data && ['soumis', 'valide'].includes(data.statut)) {
    const validee = data.statut === 'valide';
    const aCompleter = (data.documents_requis || []).filter(
      t => !(data.documents || []).some(d => d.type_doc === t.id)
    );
    return (
      <PageAssu
        titre="Votre candidature"
        sousTitre={validee ? 'Votre dossier est validé' : 'Nous vérifions vos pièces'}
      >
        <div className="dca-card" style={{ textAlign: 'center' }}>
          {validee
            ? <CheckCircle2 size={36} style={{ color: '#2e9e5b' }} />
            : <Clock size={34} style={{ color: '#f0a020' }} />}
          <h2 style={{ margin: '12px 0 8px', fontSize: 20 }}>
            {validee ? 'Dossier validé' : 'Candidature envoyée'}
          </h2>
          <p style={{ color: '#5f6b85', maxWidth: 470, margin: '0 auto' }}>
            {validee
              ? <>Félicitations {data.candidat.nom}, votre dossier est validé. Nous vous contactons pour la suite.</>
              : <>Merci {data.candidat.nom}. Votre candidature et vos pièces nous sont bien parvenues.
                  Nous les vérifions et revenons vers vous sur WhatsApp.</>}
          </p>
        </div>
        <Recapitulatif data={data} dateEnvoi={dateFr(data.soumis_at)} />

        <div className="dca-card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ flex: '1 1 220px', fontSize: 14.5, color: '#5f6b85' }}>
            Une autre candidature à enregistrer depuis cet appareil ?
          </span>
          <button
            className="dca-btn"
            onClick={nouvelleDemande}
            style={{ background: NAVY, color: '#fff', padding: '10px 16px', fontSize: 14 }}
          >
            <Plus size={16} /> Nouvelle demande
          </button>
        </div>

        {!validee && (
          <>
            <div style={titreEtape}>
              {aCompleter.length > 0 ? 'Complétez vos pièces' : 'Ajouter ou corriger une pièce'}
            </div>
            {aCompleter.length > 0 && (
              <div className="dca-card" style={{ borderColor: '#e6c98a', background: '#fdfaf3' }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <AlertCircle size={20} style={{ color: '#b8860b', flexShrink: 0, marginTop: 2 }} />
                  <div style={{ fontSize: 14.5 }}>
                    Il nous manque {aCompleter.map(t => t.label).join(', ')}.
                    Envoyez-{aCompleter.length === 1 ? 'la' : 'les'} ci-dessous, votre dossier sera complet.
                  </div>
                </div>
              </div>
            )}
            <PiecesJointes
              token={token}
              data={data}
              onMaj={(maj) => setData(prev => ({ ...prev, ...maj }))}
              apiUrl={API_URL}
            />
          </>
        )}
      </PageAssu>
    );
  }

  const refusee = data && data.statut === 'refuse';
  const formuleRetenue = formules.find(f => f.id === choix);
  const manquantes = data
    ? (data.documents_requis || []).filter(t => !(data.documents || []).some(d => d.type_doc === t.id))
    : [];
  const peutValider = !!choix && (!!token || coordonneesCompletes());

  const champs = [
    { k: 'nom', label: 'Vos nom et prénom', type: 'text', ph: 'Jean Dupont', requis: true },
    { k: 'telephone_whatsapp', label: 'Votre numéro WhatsApp', type: 'tel', ph: '+33 6 12 34 56 78', requis: true },
    { k: 'email_pro', label: 'Votre email', type: 'email', ph: 'prenom.nom@exemple.fr', requis: true },
    {
      k: 'nom_crm', label: 'Votre nom dans le CRM', type: 'text', ph: 'Jean D.', requis: false,
      aide: 'Le nom sous lequel vous êtes enregistré dans le CRM. Laissez vide si vous n\'avez pas encore de compte.'
    }
  ];

  return (
    <PageAssu
      titre="Nos conditions de rémunération"
      sousTitre="Choisissez votre formule, envoyez vos pièces, validez"
    >
      {repriseToken && !token && (
        <div className="dca-card" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <RotateCcw size={19} style={{ color: NAVY, flexShrink: 0 }} />
          <span style={{ flex: '1 1 220px', fontSize: 14.5 }}>
            Un dossier a déjà été commencé sur cet appareil. Vous pouvez le reprendre,
            ou remplir une nouvelle demande ci-dessous.
          </span>
          <button
            className="dca-btn"
            onClick={() => reprendre(repriseToken)}
            style={{ background: '#fff', color: NAVY, border: `1.5px solid ${NAVY}`, padding: '9px 15px', fontSize: 14 }}
          >
            Reprendre
          </button>
        </div>
      )}

      {dossierRetrouve && (
        <div className="dca-card" style={{ borderColor: '#a8d5b5', background: '#f4fbf6' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <CheckCircle2 size={20} style={{ color: '#2e9e5b', flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 800, color: '#1f7a45', marginBottom: 4 }}>
                Nous avons retrouvé votre dossier
              </div>
              <div style={{ fontSize: 14.5 }}>
                Vos coordonnées correspondent à une demande déjà enregistrée. Vous la
                complétez ici, il n'y a rien à recommencer et aucun nouveau lien à demander.
              </div>
            </div>
          </div>
        </div>
      )}

      {refusee && data.motif_refus && (
        <div className="dca-card" style={{ borderColor: '#e0a3a3', background: '#fdf6f6' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <AlertCircle size={20} style={{ color: '#c62828', flexShrink: 0, marginTop: 2 }} />
            <div>
              <div style={{ fontWeight: 800, color: '#c62828', marginBottom: 4 }}>Dossier à corriger</div>
              <div style={{ fontSize: 14.5 }}>{data.motif_refus}</div>
              <div style={{ fontSize: 13.5, color: '#5f6b85', marginTop: 6 }}>
                Corrigez ce qui est demandé, puis validez à nouveau.
              </div>
            </div>
          </div>
        </div>
      )}

      {formules.length === 0 ? (
        <div className="dca-card" style={{ borderColor: '#e6c98a', background: '#fdfaf3' }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <AlertCircle size={20} style={{ color: '#b8860b', flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 14.5 }}>
              Nos conditions ne sont pas encore publiées sur cette page.
              Contactez-nous sur WhatsApp, nous vous les transmettons directement.
            </div>
          </div>
        </div>
      ) : (
        <>
          {/* 1 — la formule */}
          <div style={titreEtape}>
            <span style={numeroEtape}>1</span>
            {formules.length > 1 ? 'Choisissez votre formule' : 'La formule proposée'}
          </div>
          {formules.map(f => (
            <FormuleCarte
              key={f.id}
              formule={f}
              choisie={choix === f.id}
              onChoisir={changerFormule}
              occupe={occupe === 'formule'}
            />
          ))}

          {/* 2 — les coordonnées */}
          <div style={{ ...titreEtape, opacity: choix ? 1 : 0.45 }}>
            <span style={numeroEtape}>2</span> Vos coordonnées
          </div>
          <div className="dca-card">
            {token ? (
              <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                <CheckCircle2 size={20} style={{ color: '#2e9e5b', flexShrink: 0, marginTop: 2 }} />
                <div style={{ fontSize: 14.5 }}>
                  <div style={{ fontWeight: 700 }}>{data?.candidat?.nom}</div>
                  <div style={{ color: '#5f6b85' }}>
                    {data?.candidat?.telephone_whatsapp} · {data?.candidat?.email_pro}
                  </div>
                  {data?.candidat?.nom_crm && (
                    <div style={{ color: '#5f6b85' }}>CRM : {data.candidat.nom_crm}</div>
                  )}
                  <div style={{ color: '#8a94ad', fontSize: 12.5, marginTop: 5, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Pencil size={12} /> Une erreur ? Signalez-la nous sur WhatsApp.
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div style={{ fontSize: 14.5, color: '#5f6b85', marginBottom: 16 }}>
                  {choix
                    ? <>Formule retenue : <strong style={{ color: NAVY }}>{formuleRetenue?.nom}</strong>.
                        Laissez vos coordonnées pour passer à l'envoi de vos documents.</>
                    : <>Cochez d'abord une formule ci-dessus, puis laissez vos coordonnées.</>}
                </div>
                <div>
                  {champs.map(c => (
                    <div key={c.k} style={{ marginBottom: 13 }}>
                      <label style={{ display: 'block', fontSize: 13, fontWeight: 700, color: NAVY, marginBottom: 5 }}>
                        {c.label}
                        {!c.requis && (
                          <span style={{ color: '#8a94ad', fontWeight: 500 }}> (facultatif)</span>
                        )}
                      </label>
                      {c.aide && (
                        <div style={{ fontSize: 12.5, color: '#8a94ad', marginBottom: 5 }}>{c.aide}</div>
                      )}
                      <input
                        className="dca-champ"
                        type={c.type}
                        required={c.requis}
                        placeholder={c.ph}
                        value={form[c.k]}
                        onChange={(e) => setForm({ ...form, [c.k]: e.target.value })}
                        onBlur={ouvrirDossier}
                      />
                    </div>
                  ))}
                  {occupe === 'ouverture' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#5f6b85', fontSize: 14 }}>
                      <Loader2 size={16} className="dca-spin" /> Enregistrement…
                    </div>
                  )}
                  {erreur && !token && <div style={encadreErreur}>{erreur}</div>}
                </div>
              </>
            )}
          </div>

          {/* 3 — les pièces */}
          <div style={{ ...titreEtape, opacity: token ? 1 : 0.45 }}>
            <span style={numeroEtape}>3</span> Envoyez vos documents
          </div>
          {token && data ? (
            <PiecesJointes
              token={token}
              data={data}
              onMaj={(maj) => { setData(prev => ({ ...prev, ...maj })); setManquants([]); }}
              manquants={manquants}
              apiUrl={API_URL}
            />
          ) : (
            <div className="dca-card" style={{ color: '#5f6b85', fontSize: 14.5 }}>
              Vos coordonnées d'abord, la liste des pièces à fournir s'affichera ici.
            </div>
          )}

          {/* 4 — le récapitulatif et la validation */}
          <div style={{ ...titreEtape, opacity: token ? 1 : 0.45 }}>
            <span style={numeroEtape}>4</span> Vérifiez et validez
          </div>
          {token && data ? (
            <>
              <ChampNomCrm
                token={token}
                apiUrl={API_URL}
                valeur={data.candidat?.nom_crm}
                onMaj={(maj) => setData(prev => ({ ...prev, ...maj }))}
              />
              <Recapitulatif data={data} />
            </>
          ) : (
            <div className="dca-card" style={{ color: '#5f6b85', fontSize: 14.5 }}>
              Votre récapitulatif apparaîtra ici. Vous pouvez valider dès maintenant
              et envoyer vos pièces plus tard.
            </div>
          )}

          <BlocValidation
            peutValider={peutValider}
            accepte={accepte}
            setAccepte={setAccepte}
            erreur={erreur}
            occupe={occupe === 'validation' || occupe === 'ouverture'}
            onValider={valider}
            avertissement={
              !choix ? 'Cochez la formule qui vous convient pour pouvoir valider.'
                : !peutValider ? 'Complétez vos nom, numéro WhatsApp et email pour pouvoir valider.'
                : manquantes.length > 0
                  ? `Vous validez sans ${manquantes.length === 1 ? 'la pièce suivante' : 'les pièces suivantes'} : ${manquantes.map(m => m.label).join(', ')}. Vous pourrez ${manquantes.length === 1 ? 'l\'' : 'les '}envoyer plus tard depuis ce lien.`
                  : null
            }
          />
        </>
      )}
    </PageAssu>
  );
}

export default ConditionsAssu;
